import type { QueryClient } from "@tanstack/react-query"
import { isRedirect } from "@tanstack/react-router"
import { assertSessionVersion, getSessionVersion } from "@/shared/lib/auth"
import { currentIdentity, type Resolution } from "./identity"
import { isTransientFailure } from "./user-query"

interface ResolveUserParams {
	queryClient: QueryClient
	locationSearch: Record<string, unknown>
	fresh?: boolean
}

export async function resolveUser({
	queryClient,
	locationSearch,
	fresh = false,
}: ResolveUserParams): Promise<Resolution> {
	const identity = currentIdentity()
	let version = getSessionVersion()

	const id = await identity.resolveId(locationSearch)
	if (id === null) return { status: "anonymous" }
	assertSessionVersion(version)

	if (identity.prepare) {
		await identity.prepare(id)
		version = getSessionVersion()
	}

	try {
		const user = await identity.load(queryClient, id, version, fresh)
		assertSessionVersion(version)
		return { status: "resolved", user }
	} catch (error) {
		if (isRedirect(error)) throw error
		assertSessionVersion(version)
		console.error("[resolveUser] failed:", error)
		if (isTransientFailure(error)) throw error
		return identity.reject(queryClient, id)
	}
}
